import { getPropertyAddress } from '../../../../services/property-address';
import type {
  PropertyCartItem,
  AssetCartItem,
  AssetRef,
  ServiceInfo,
  PropertyAccess,
  EntityInfo,
} from '../../cart/types/cart.types';
import type { AccessType } from '../../cart/model/CartModel';

// ---------------------------------------------------------------------------
// Types for raw order items coming from the API
// ---------------------------------------------------------------------------

export interface OrderItem {
  propertyId?: string;
  assetUUID?: string;
  property?: Partial<Record<string, unknown>>;
  services?: Array<{
    lineItemUUID?: string;
    shoppingCartSlug?: string;
    slug?: string;
    quantity?: number;
    occupancyStatus?: string;
    name?: string;
    price?: number;
    totalInitialPrice?: number;
    onlineImage?: string;
  }>;
  propertyAccess?: {
    onsiteContactName?: string;
    onsiteContactPhone?: string;
    accessType?: string;
    accessCode?: string;
    lockboxLocation?: string;
    rentlyRegistrationPin?: string;
  };
  specialInstructions?: string;
  propertyPrice?: number;
  firstAvailableDate?: string;
  isPropertyReady?: boolean;
  orderNumber?: string;
  assetNote?: string;
  assetUrl?: string;
  // DV-2131 grouped model: a placed asset order carries the full photo list.
  // The backend derives the singular assetUUID/assetUrl from assetRefs[0], so
  // these refs are the source of truth for the confirmation-table fan-out.
  assetRefs?: AssetRef[];
}

export interface BulkOrderResponseData {
  items: OrderItem[];
  serviceImageMap?: Record<string, string>;
  entityMap?: Record<string, EntityInfo>;
}

// ---------------------------------------------------------------------------
// Transform: API orders -> PropertyCartItem[]
// ---------------------------------------------------------------------------

export function transformToPropertyCartItems(
  orders: OrderItem[],
  serviceImageMap?: Record<string, string>,
  entityMap?: Record<string, EntityInfo>,
): PropertyCartItem[] {
  if (!orders || orders.length === 0) return [];

  return orders.map((order) => {
    const services: ServiceInfo[] | null = order.services
      ? order.services.map((svc) => {
          let imageFromMap: string | null = null;
          if (serviceImageMap) {
            imageFromMap =
              (svc.slug ? serviceImageMap[svc.slug] : null) ||
              (svc.shoppingCartSlug
                ? serviceImageMap[svc.shoppingCartSlug]
                : null) ||
              (svc.name ? serviceImageMap[svc.name] : null) ||
              null;
          }

          return {
            lineItemUUID: svc.lineItemUUID || '',
            shoppingCartSlug: svc.shoppingCartSlug || svc.slug || '',
            quantity: svc.quantity ?? null,
            occupancyStatus: svc.occupancyStatus ?? null,
            name: svc.name || '',
            price: svc.price || 0,
            totalInitialPrice: svc.totalInitialPrice || svc.price || 0,
            onlineImage: svc.onlineImage || imageFromMap || null,
            slug: svc.slug,
          };
        })
      : null;

    let propertyAccess: PropertyAccess | null = null;
    if (order.propertyAccess) {
      propertyAccess = {
        onsiteContactName: order.propertyAccess.onsiteContactName || '',
        onsiteContactPhone: order.propertyAccess.onsiteContactPhone || '',
        accessType:
          (order.propertyAccess.accessType as AccessType) || 'Keypad',
        accessCode: order.propertyAccess.accessCode,
        lockboxLocation: order.propertyAccess.lockboxLocation,
        rentlyRegistrationPin: order.propertyAccess.rentlyRegistrationPin,
      };
    }

    return {
      propertyId: order.propertyId || '',
      property: order.property || {},
      services,
      propertyAccess,
      specialInstructions: order.specialInstructions ?? null,
      price: order.propertyPrice || 0,
      firstAvailableDate: order.firstAvailableDate ?? null,
      isPropertyReady: order.isPropertyReady ?? null,
      formattedAddress: getPropertyAddress(order),
      orderNumber: order.orderNumber || '',
      entityUUID: entityMap?.[order.propertyId || '']?.entityUUID,
      entityName: entityMap?.[order.propertyId || '']?.entityName,
    };
  });
}

// ---------------------------------------------------------------------------
// Transform: API orders -> AssetCartItem[]
// ---------------------------------------------------------------------------

export function transformToAssetCartItems(
  orders: OrderItem[],
  entityMap?: Record<string, EntityInfo>,
): AssetCartItem[] {
  if (!orders || orders.length === 0) return [];

  return orders.map((order) => {
    const firstService = order.services?.[0];
    const service: ServiceInfo = firstService
      ? {
          lineItemUUID: firstService.lineItemUUID || '',
          shoppingCartSlug: firstService.shoppingCartSlug || '',
          quantity: firstService.quantity ?? null,
          occupancyStatus: firstService.occupancyStatus ?? null,
          name: firstService.name || 'Image Edit',
          price: firstService.price || 0,
          totalInitialPrice:
            firstService.totalInitialPrice || firstService.price || 0,
          onlineImage: firstService.onlineImage ?? null,
        }
      : {
          lineItemUUID: '',
          shoppingCartSlug: '',
          quantity: null,
          occupancyStatus: null,
          name: 'Image Edit',
          price: 0,
          totalInitialPrice: 0,
          onlineImage: null,
        };

    return {
      assetId: order.assetUUID || order.propertyId || '',
      service,
      // Forward the photo list so AssetCartTable fans out multi-photo orders;
      // singular assetId/assetUrl stay as the back-compat fallback.
      assetRefs: order.assetRefs,
      additionalNotes: order.assetNote || '',
      price: order.propertyPrice || 0,
      assetUrl: order.assetUrl || '',
      propertyUUID: order.propertyId || '',
      propertyAddress: getPropertyAddress(order),
      orderNumber: order.orderNumber || '',
      entityUUID: entityMap?.[order.propertyId || '']?.entityUUID,
      entityName: entityMap?.[order.propertyId || '']?.entityName,
    };
  });
}
